<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import MailingListsAPI from 'dashboard/api/mailingLists';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const mailingLists = ref([]);
const isLoading = ref(false);
const updatingListId = ref(null);

const contactId = computed(() => props.contact?.id);
const contactEmail = computed(() => props.contact?.email);
const isAdministrator = computed(
  () => store.getters.getCurrentRole === 'administrator'
);

const normalizedSubscriptions = computed(() => {
  const subscriptions =
    props.contact?.mailing_list_subscriptions ||
    props.contact?.mailingListSubscriptions ||
    [];

  return subscriptions.map(subscription => ({
    id: subscription.id,
    status: subscription.status,
    mailingListId: subscription.mailing_list_id || subscription.mailingListId,
    mailingListName:
      subscription.mailing_list_name || subscription.mailingListName,
    mailingListTerms:
      subscription.mailing_list_terms || subscription.mailingListTerms,
    consentedAt: subscription.consented_at || subscription.consentedAt,
    unsubscribedAt: subscription.unsubscribed_at || subscription.unsubscribedAt,
    preferenceUpdatedAt:
      subscription.preference_updated_at || subscription.preferenceUpdatedAt,
  }));
});

const subscriptionsByListId = computed(() => {
  return normalizedSubscriptions.value.reduce((subscriptions, subscription) => {
    subscriptions.set(Number(subscription.mailingListId), subscription);
    return subscriptions;
  }, new Map());
});

const activeLists = computed(() =>
  mailingLists.value.filter(list => list.status === 'active')
);

const mailingListEntries = computed(() => {
  const entries = activeLists.value.map(list => ({
    id: Number(list.id),
    name: list.name,
    terms: list.terms,
    subscription: subscriptionsByListId.value.get(Number(list.id)),
  }));

  normalizedSubscriptions.value.forEach(subscription => {
    const listId = Number(subscription.mailingListId);
    if (!entries.some(entry => entry.id === listId)) {
      entries.push({
        id: listId,
        name: subscription.mailingListName,
        terms: subscription.mailingListTerms,
        subscription,
      });
    }
  });

  return entries;
});

const canManageLists = computed(
  () => isAdministrator.value && !!contactId.value && !!contactEmail.value
);

const loadMailingLists = async () => {
  isLoading.value = true;
  try {
    const {
      data: { payload = [] },
    } = await MailingListsAPI.get();
    mailingLists.value = payload;
  } catch (error) {
    useAlert(t('CONTACT_PANEL.MAILING_LISTS.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const refreshContact = async () => {
  if (contactId.value) {
    await store.dispatch('contacts/show', { id: contactId.value });
  }
};

const statusLabel = status => {
  switch (status) {
    case 'subscribed':
      return t('CONTACT_PANEL.MAILING_LISTS.STATUS.SUBSCRIBED');
    case 'unsubscribed':
      return t('CONTACT_PANEL.MAILING_LISTS.STATUS.UNSUBSCRIBED');
    case 'pending':
      return t('CONTACT_PANEL.MAILING_LISTS.STATUS.PENDING');
    default:
      return t('CONTACT_PANEL.MAILING_LISTS.STATUS.NOT_SUBSCRIBED');
  }
};

const statusClass = status => {
  switch (status) {
    case 'subscribed':
      return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
    case 'unsubscribed':
      return 'bg-ruby-500/10 text-ruby-600 dark:text-ruby-400';
    case 'pending':
      return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    default:
      return 'bg-n-alpha-2 text-n-slate-11';
  }
};

const timestampLabel = subscription => {
  if (!subscription) return '';

  if (subscription.unsubscribedAt) {
    return `${t('CONTACT_PANEL.MAILING_LISTS.UNSUBSCRIBED_AT')} ${dynamicTime(
      subscription.unsubscribedAt
    )}`;
  }

  if (subscription.preferenceUpdatedAt) {
    return `${t('CONTACT_PANEL.MAILING_LISTS.UPDATED_AT')} ${dynamicTime(
      subscription.preferenceUpdatedAt
    )}`;
  }

  if (subscription.consentedAt) {
    return `${t('CONTACT_PANEL.MAILING_LISTS.CONSENTED_AT')} ${dynamicTime(
      subscription.consentedAt
    )}`;
  }

  return '';
};

const actionLabel = entry => {
  if (entry.subscription?.status === 'subscribed') {
    return t('CONTACT_PANEL.MAILING_LISTS.ACTIONS.UNSUBSCRIBE');
  }

  if (entry.subscription?.status === 'unsubscribed') {
    return t('CONTACT_PANEL.MAILING_LISTS.ACTIONS.RESUBSCRIBE');
  }

  return t('CONTACT_PANEL.MAILING_LISTS.ACTIONS.SUBSCRIBE');
};

const actionColor = entry =>
  entry.subscription?.status === 'subscribed' ? 'ruby' : 'teal';

const updateSubscription = async entry => {
  if (!canManageLists.value || updatingListId.value) return;

  updatingListId.value = entry.id;

  try {
    if (entry.subscription?.status === 'subscribed') {
      await MailingListsAPI.unsubscribe(
        entry.id,
        entry.subscription.id,
        t('CONTACT_PANEL.MAILING_LISTS.ADMIN_UNSUBSCRIBE_REASON')
      );
      useAlert(t('CONTACT_PANEL.MAILING_LISTS.SUCCESS.UNSUBSCRIBE'));
    } else {
      await MailingListsAPI.subscribe(entry.id, [contactId.value]);
      useAlert(t('CONTACT_PANEL.MAILING_LISTS.SUCCESS.SUBSCRIBE'));
    }

    await refreshContact();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.MAILING_LISTS.ERROR'));
  } finally {
    updatingListId.value = null;
  }
};

onMounted(() => {
  loadMailingLists();
});
</script>

<template>
  <section
    class="w-full p-3 mt-2 rounded-lg border border-n-weak bg-n-alpha-1"
    data-testid="contact-mailing-list-preferences"
  >
    <div class="mb-2 flex items-center justify-between gap-2">
      <h4 class="m-0 text-xs font-medium uppercase text-n-slate-10">
        {{ t('CONTACT_PANEL.MAILING_LISTS.TITLE') }}
      </h4>
      <span v-if="isLoading" class="text-xs text-n-slate-10">
        {{ t('CONTACT_PANEL.MAILING_LISTS.LOADING') }}
      </span>
    </div>

    <p v-if="!contactEmail" class="m-0 text-xs leading-5 text-n-slate-10">
      {{ t('CONTACT_PANEL.MAILING_LISTS.NO_EMAIL') }}
    </p>

    <p
      v-if="!isLoading && !mailingListEntries.length"
      class="m-0 text-xs leading-5 text-n-slate-10"
    >
      {{ t('CONTACT_PANEL.MAILING_LISTS.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="entry in mailingListEntries"
        :key="entry.id"
        class="pb-2 border-b border-n-weak last:border-0 last:pb-0"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <div class="text-sm font-medium truncate text-n-slate-12">
              {{ entry.name }}
            </div>
            <p
              v-if="entry.terms"
              class="mt-1 mb-0 text-xs leading-5 text-n-slate-10"
            >
              {{ entry.terms }}
            </p>
          </div>
          <span
            class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
            :class="statusClass(entry.subscription?.status)"
          >
            {{ statusLabel(entry.subscription?.status) }}
          </span>
        </div>

        <div
          v-if="timestampLabel(entry.subscription)"
          class="mt-1 text-xs text-n-slate-10"
        >
          {{ timestampLabel(entry.subscription) }}
        </div>

        <div class="mt-2 flex justify-end">
          <Button
            :label="actionLabel(entry)"
            :color="actionColor(entry)"
            :is-loading="updatingListId === entry.id"
            :disabled="!canManageLists || !!updatingListId"
            size="xs"
            outline
            @click="updateSubscription(entry)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
